import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../ui/Icon';
import './TodayMissions.css';

// Today's mission: one card, not three loose rows. `missions` is exactly playerProgressService's `missions`
// array ({ id, label, description, complete }) — `complete` is recomputed from real activity on every fetch.
// The only things added here are presentation: an icon, a rough effort estimate and the screen that does the job.
// Effort is an ESTIMATE of a typical session (a 10-question quick session ≈ 5 minutes), labelled as such.
//
// `personalizedJourneyEligible` (from player.personalized_journey_eligible, computed server-side — see
// playerProgressService.getPlayerProgress) retargets "Practice today" once the journey is unlocked, so the student
// sees ONE obvious next action instead of two competing ones (audit ST-09). Nothing about which missions exist or
// when they're "complete" changes — any session still satisfies this mission, staged or not.
const META = {
  practice_today: { icon: 'practice', minutes: 5, cta: 'Start practice', go: () => ({ path: '/student/mock', state: { preselectFlow: 'quick' } }) },
  recover_mistake: { icon: 'mistake', minutes: 3, cta: 'Recover a mistake', go: () => ({ path: '/student/mistakes' }) },
  beat_best: { icon: 'trophy', minutes: 10, cta: 'Take a challenge', go: () => ({ path: '/student/practice' }) },
};
const journeyMeta = { icon: 'sparkle', minutes: 5, cta: 'Continue your journey', go: () => ({ path: '/student/mock', state: { preselectFlow: 'personalized' } }) };

export default function TodayMissions({ missions = [], personalizedJourneyEligible = false }) {
  const navigate = useNavigate();
  if (!missions.length) return null;

  const done = missions.filter(m => m.complete).length;
  const total = missions.length;
  const allDone = done === total;
  const next = missions.find(m => !m.complete);
  const pct = Math.round((done / total) * 100);

  const metaFor = (m) => (m.id === 'practice_today' && personalizedJourneyEligible ? journeyMeta : (META[m.id] || META.practice_today));

  const start = (m) => {
    const t = metaFor(m).go();
    navigate(t.path, t.state ? { state: t.state } : undefined);
  };

  return (
    <div className={`z-tm${allDone ? ' z-tm--done' : ''}`}>
      <div className="z-tm-head">
        <div className="z-tm-headtext">
          <span className="z-tm-eyebrow"><Icon name="mission" size={14} /> Today's mission</span>
          <span className="z-tm-title">{allDone ? 'All done — your streak is safe' : next ? next.label : 'Keep going'}</span>
          <span className="z-tm-sub">{allDone ? 'Come back tomorrow for a fresh set.' : next?.description}</span>
        </div>
        <div className="z-tm-count" role="img" aria-label={`${done} of ${total} missions complete`}>
          <b>{done}</b><span>/{total}</span>
        </div>
      </div>

      <div className="z-tm-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Missions completed today">
        <span style={{ width: `${pct}%` }} />
      </div>

      <ul className="z-tm-list">
        {missions.map(m => {
          const meta = metaFor(m);
          const isNext = next && next.id === m.id;
          return (
            <li key={m.id} className={`z-tm-item${m.complete ? ' z-tm-item--done' : ''}${isNext ? ' z-tm-item--next' : ''}`}>
              <span className="z-tm-icon" aria-hidden="true"><Icon name={m.complete ? 'check' : meta.icon} size={18} strokeWidth={m.complete ? 3 : 2} /></span>
              <span className="z-tm-body">
                <span className="z-tm-label">{m.label}</span>
                <span className="z-tm-desc">{m.description}</span>
              </span>
              {m.complete ? (
                <span className="z-tm-status">Done</span>
              ) : isNext ? (
                <button type="button" className="z-btn z-btn--primary z-btn--sm z-tm-cta" onClick={() => start(m)}>
                  {meta.cta}
                </button>
              ) : (
                <span className="z-tm-est"><Icon name="timer" size={12} /> ~{meta.minutes} min</span>
              )}
              <span className="z-sr-only">{m.complete ? 'Completed' : 'Not completed yet'}</span>
            </li>
          );
        })}
      </ul>
      {!allDone && next && <p className="z-tm-foot"><Icon name="timer" size={12} /> About {metaFor(next).minutes} minutes · estimate for a typical session</p>}
    </div>
  );
}
